// packages/common-ui/src/utils/homepageDataLayerService.ts
import { detectHomepageAnalytics } from './analyticsDetection';
import { adobeDataLayer } from './AdobeDataLayer';
import { CmsContentTypeId } from 'tsconfig/types/Contentful.types';
import type { DLClickEventHandlerProps } from 'tsconfig/types';
import { isBrowser, logger } from '../..';

export interface HomepageClickEventProps {
  contentItem: Record<string, unknown>;
  layoutIndex?: number;
  componentType?: CmsContentTypeId;
  data: string;
  additionalProps?: {
    position?: number;
    columnTitle?: string;
  };
}

export interface HomepageDataLayerConfig {
  renderInPageLevel?: boolean | ((contentType?: CmsContentTypeId) => boolean);
  fallbackClickHandler?: DLClickEventHandlerProps;
}

type ClickEventHandler = (data: string, additionalProps?: { position?: number; columnTitle?: string }) => void;
type CarouselClickEventHandler = (data: string, position: number) => void;
type ColumnClickEventHandler = (data: string) => void;

/**
 * Service class to handle homepage analytics data layer events
 * Extracted from RenderDynamicComponent to separate concerns
 */
export class HomepageDataLayerService {
  private config: HomepageDataLayerConfig;

  constructor(config: HomepageDataLayerConfig = {}) {
    this.config = config;
  }

  /**
   * Helper function for column title detection (simplified version for columns)
   */
  private getColumnTitle(columnEntryTitle?: string): string {
    if (!columnEntryTitle) {
      return 'unknown';
    }
    const analyticsResult = detectHomepageAnalytics({
      contentItem: { fields: { entryTitle: columnEntryTitle } },
      layoutIndex: undefined,
      componentType: CmsContentTypeId.Column,
    });
    return analyticsResult?.sectionName || 'unknown';
  }

  /**
   * Helper function to get page info for analytics
   */
  private getPageInfo(): { page: string; pageName: string } {
    const pageInfo = adobeDataLayer.getPageInfo();
    let page: string, pageName: string;
    if (pageInfo.pageName === 'homepage') {
      page = 'home';
      pageName = 'Homepage';
    } else if (pageInfo.pageName === 'next aarp' || pageInfo.pageName === 'next wine deals') {
      page = 'cmlp';
      pageName = 'CMLP';
    } else {
      page = pageInfo.pageName;
      pageName = 'Content Page';
    }
    return { page, pageName };
  }

  /**
   * Handles click events for homepage components with analytics detection
   */
  handleHomepageClickEvent(props: HomepageClickEventProps): void {
    const { contentItem, layoutIndex, componentType, data, additionalProps = {} } = props;
    const { page, pageName } = this.getPageInfo();
    const { renderInPageLevel, fallbackClickHandler } = this.config;
    const shouldTrackInPageLevel =
      typeof renderInPageLevel === 'function' ? renderInPageLevel(componentType) : renderInPageLevel;

    if (shouldTrackInPageLevel && componentType) {
      // Use hybrid analytics detection
      const analyticsResult = detectHomepageAnalytics({
        contentItem,
        layoutIndex,
        componentType,
      });

      const { sectionName: section, eventType } = analyticsResult || {
        sectionName: 'unknown',
        eventType: 'homeSectionBanner' as const,
      };

      const clickName = section === 'hero banner' ? 'hero banner click' : 'home sections click';
      const position = additionalProps.position && section === 'hero banner' ? ` ${additionalProps.position}` : '';

      // Log detection method in development
      if (process.env.NODE_ENV === 'development' && analyticsResult) {
        logger.info(`[${pageName} Analytics]`, {
          section,
          eventType,
          method: analyticsResult.detectionMethod,
          confidence: analyticsResult.confidence,
          ruleId: analyticsResult.metadata?.ruleId,
          entryTitle: (contentItem?.fields as Record<string, unknown>)?.entryTitle,
        });
      }

      // Handle different component types
      this.trackComponentClick({
        componentType,
        eventType,
        page,
        section,
        position,
        data,
        clickName,
        additionalProps,
        isSectionDetectionReliable:
          !!analyticsResult &&
          (analyticsResult.detectionMethod === 'cms-explicit' || analyticsResult.detectionMethod === 'rules-engine'),
      });
    } else {
      // Fallback to provided click handler
      fallbackClickHandler?.(data, componentType);
    }
  }

  /**
   * Tracks click events based on component type
   */
  private trackComponentClick({
    componentType,
    eventType,
    page,
    section,
    position,
    data,
    clickName,
    additionalProps,
    isSectionDetectionReliable: isParentSectionReliable,
  }: {
    componentType: CmsContentTypeId;
    eventType: 'homeHeroBanner' | 'homeSectionBanner';
    page: string;
    section: string;
    position: string;
    data: string;
    clickName: string;
    additionalProps: { position?: number; columnTitle?: string };
    isSectionDetectionReliable: boolean;
  }): void {
    const splitContent = data?.split(':') || [],
      clickText = (splitContent[0] || '').trim(),
      elementType = (splitContent[1] || '').trim(),
      cardTitle = (splitContent[2] || '').trim(),
      destinationUrl = splitContent.slice(3).join(':').trim() || (isBrowser() ? window.location.href : '/');
    if (page === 'cmlp') {
      const cmlpClickEventDetails = {
        clickAction: destinationUrl,
        clickLocation: 'Sale',
        clickDescription: `Sale:${clickText || 'not set'}:${elementType}:${cardTitle}`,
        clickText: clickText || 'not set',
        clickName: elementType,
        cardTitle,
      };
      // CMLP Click Event
      if (process.env.NODE_ENV === 'development') {
        logger.info(`[CMLP Event Details]`, cmlpClickEventDetails);
      }
      adobeDataLayer.clickEvent('cmlpClick', cmlpClickEventDetails);
    } else if (componentType === CmsContentTypeId.Carousel) {
      adobeDataLayer.clickEvent(eventType, {
        clickDescription: `${page}:${section}${position}:${clickText}`,
        clickName,
        clickLocation: `${section}${position}`,
        clickText: clickText || data,
        clickAction: 'navigation',
      });
    } else {
      const columnTitle =
        additionalProps?.columnTitle && additionalProps.columnTitle !== 'unknown'
          ? additionalProps.columnTitle
          : section;
      const sectionName = isParentSectionReliable ? section : columnTitle;
      adobeDataLayer.clickEvent(eventType, {
        clickDescription: `${page}:${sectionName}${position}:${clickText}`,
        clickName,
        clickLocation: `${sectionName}${position}`,
        clickText: clickText || data,
        clickAction: 'navigation',
      });
    }
  }

  /**
   * Creates a standard click event handler that matches DLClickEventHandlerProps interface
   */
  createStandardClickEventHandler(
    contentItem: Record<string, unknown>,
    layoutIndex?: number,
    componentType?: CmsContentTypeId,
  ): DLClickEventHandlerProps {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return (data: string, clickType?: string): void => {
      // For standard handlers, we don't use clickType but maintain interface compatibility
      this.handleHomepageClickEvent({
        contentItem,
        layoutIndex,
        componentType,
        data,
        additionalProps: {},
      });
    };
  }

  /**
   * Creates a click event handler function for a specific component type
   */
  createClickEventHandler(
    contentItem: Record<string, unknown>,
    layoutIndex?: number,
    componentType?: CmsContentTypeId,
  ): ClickEventHandler {
    return (data: string, additionalProps: { position?: number; columnTitle?: string } = {}): void => {
      this.handleHomepageClickEvent({
        contentItem,
        layoutIndex,
        componentType,
        data,
        additionalProps,
      });
    };
  }

  /**
   * Creates a click event handler for carousel components with position tracking
   */
  createCarouselClickEventHandler(
    contentItem: Record<string, unknown>,
    layoutIndex?: number,
  ): CarouselClickEventHandler {
    return (data: string, position: number): void => {
      this.handleHomepageClickEvent({
        contentItem,
        layoutIndex,
        componentType: CmsContentTypeId.Carousel,
        data,
        additionalProps: { position },
      });
    };
  }

  /**
   * Creates a click event handler for column components with column title detection
   */
  createColumnClickEventHandler(
    contentItem: Record<string, unknown>,
    columnContent: Record<string, unknown>,
  ): ColumnClickEventHandler {
    const columnTitle = this.getColumnTitle((columnContent?.fields as Record<string, unknown>)?.entryTitle as string);
    return (data: string): void => {
      this.handleHomepageClickEvent({
        contentItem,
        layoutIndex: undefined,
        componentType: CmsContentTypeId.Column,
        data,
        additionalProps: { columnTitle },
      });
    };
  }

  /**
   * Creates a click event handler for container components
   */
  createContainerEventHandler(
    contentItem: Record<string, unknown>,
    entryTitle: string,
  ): (data: string, contentType?: string) => void {
    return (data: string, contentType?: string): void => {
      if (contentType === CmsContentTypeId.Link) {
        const linkHandler = this.createStandardClickEventHandler(
          contentItem,
          undefined,
          contentType as CmsContentTypeId,
        );
        linkHandler(`${entryTitle} - ${data}`);
      } else {
        const standardHandler = this.createStandardClickEventHandler(
          contentItem,
          undefined,
          contentType as CmsContentTypeId,
        );
        standardHandler(data);
      }
    };
  }

  /**
   * Updates the service configuration
   */
  updateConfig(config: Partial<HomepageDataLayerConfig>): void {
    this.config = { ...this.config, ...config };
  }
}

/**
 * Factory function to create a homepage data layer service with configuration
 */
export const createHomepageDataLayerService = (config: HomepageDataLayerConfig): HomepageDataLayerService =>
  new HomepageDataLayerService(config);
